import { usePageDrawer } from '@app/components/page/drawer/PageDrawerContext';
import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { CreateFragmentForm } from '@app/modules/fragments/screens/add/CreateFragmentForm';
import { useStreamSelection } from '@app/modules/logger/screens/stream/stores/useStreamSelection';
import { toDateTimeStr } from '@mi-front/core';
import { useCallback } from 'react';
import { useAllowFragmentExternalExport } from '../../hooks/useAllowFragmentExternalExport';
import { useFragmentCreate } from '../../hooks/useFragmentCreate';
import { useFragmentCreationStore } from '../../stores/fragment-creation.store';
import { CreateFragmentFormValues } from './useFragmentForm';
import { Station } from '@app/core/types/stations';

type Props = {
  station: Station;
};

export const CreateFragmentFromLoggerPage = (p: Props) => {
  const { station } = p;
  const { closeDrawer } = usePageDrawer();
  const capture = usePostHogCapture({ domain: 'create_fragment' });
  const create = useFragmentCreate();
  const allowExternalExport = useAllowFragmentExternalExport(station);
  const { exportEnabled, toggleExport, getFormValues, setFormValues, clearFormValues } =
    useFragmentCreationStore();
  const { selectionExists, selectionRange, clearSelection } = useStreamSelection();

  // Get persisted form values for this station
  const persistedValues = getFormValues(station.id);

  async function handleCreate(data: CreateFragmentFormValues) {
    if (!selectionExists) {
      return null;
    }

    const saveData = {
      ...data,
      type: 'DEFAULT_FRAGMENT',
      stationId: station.id,
      from: toDateTimeStr(selectionRange.from),
      to: toDateTimeStr(selectionRange.to),
      tags: data.tags,
    };
    await create(saveData);

    // Update the store with the export preference after successful creation
    toggleExport(data.doExternalExport);

    // Clear form values for this station after successful creation
    clearFormValues(station.id);

    // Clear the selection after successful creation
    clearSelection();

    capture('save', { data: saveData });
    closeDrawer();
  }

  const handleChangedValues = useCallback(
    (values: Partial<CreateFragmentFormValues>) => {
      setFormValues(station.id, values);
    },
    [station.id, setFormValues],
  );

  const handleDiscard = useCallback(() => {
    capture('discard', { station_id: station.id });
    clearFormValues(station.id);
    closeDrawer();
  }, [capture, closeDrawer, clearFormValues, station.id]);

  const handleDraft = useCallback(() => {
    capture('draft', { station_id: station.id });
    closeDrawer();
  }, [capture, closeDrawer, station.id]);

  const initialValues = {
    ...persistedValues,
    tags: persistedValues?.tags ?? [],
    doExternalExport: allowExternalExport && (exportEnabled ?? persistedValues?.doExternalExport),
  };

  return (
    <CreateFragmentForm
      station={station}
      initialValues={initialValues}
      allowExternalExport={allowExternalExport}
      onCreate={handleCreate}
      onDiscard={handleDiscard}
      onDraft={handleDraft}
      enableCreate={selectionExists}
      onChangedValues={handleChangedValues}
      selection={selectionExists ? selectionRange : undefined}
    />
  );
};
